import { useState, FormEvent } from 'react';
import { siteInfo } from '@/mocks/site';
import SEO from '@/components/feature/SEO';
import { OrganizationSchema, WebsiteSchema, WebPageSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9qog7snlsngrm4lm310';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('success');
          setFormMessage('感谢您的留言，我们会尽快回复！');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们"
        description={`了解${siteInfo.name}的品牌故事、文化使命与核心团队。${siteInfo.description}`}
        keywords="高义白洁,关于我们,品牌介绍,传统文化网站,联系我们"
        canonicalPath="/about"
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <WebPageSchema
        pageUrl="/about"
        pageTitle={`关于我们 - ${siteInfo.name}`}
        pageDescription={`了解${siteInfo.name}的品牌故事、文化使命与核心团队。`}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: '/' },
          { name: '关于我们' },
        ]}
      />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="w-full py-14 md:py-20 bg-background-50">
            <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-16">
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
                  关于我们
                </h1>
                <p className="text-foreground-600 max-w-2xl mx-auto text-lg leading-relaxed">
                  传承中华美德，弘扬高义白洁精神
                </p>
              </div>

              <div className="mb-16">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">
                  品牌介绍
                </h2>
                <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    <strong className="text-foreground-900">高义白洁</strong>是一个专注于中华传统美德研究与传播的文化平台。我们致力于深度挖掘"高义"与"白洁"这两个核心理念的文化内涵，通过系统的研究、优质的原创内容和多元的传播方式，让更多人理解并践行这一崇高品格。
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    我们的团队由来自文学、历史、哲学等领域的研究者和爱好者组成。大家怀着对传统文化的热爱和对美德传播的使命感汇聚在一起，希望在这个物质丰裕但精神偶有迷失的时代，为读者点亮一盏心灵的明灯。
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    自成立以来，我们已经发表了上百篇深度原创文章，内容涵盖经典解读、人物传记、修养方法、现代应用等多个维度，累计服务读者超过五十万人次。我们坚信，高义白洁的精神永远不会过时，它将在每一个时代焕发出新的光芒。
                  </p>
                </div>
              </div>

              <div className="mb-16">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">
                  核心价值
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {[
                    {
                      icon: 'ri-book-2-line',
                      title: '学术严谨',
                      desc: '每篇文章都经过严格考证和多重审核，确保内容的准确性和权威性，为读者提供值得信赖的文化知识。',
                    },
                    {
                      icon: 'ri-heart-pulse-line',
                      title: '传播善意',
                      desc: '我们不仅传播知识，更传递向善的力量，帮助每一位读者在浮躁的社会中找到内心的安宁与方向。',
                    },
                    {
                      icon: 'ri-group-line',
                      title: '服务读者',
                      desc: '以读者为中心，用通俗易懂的语言解读深奥的文化概念，让传统文化走进现代人的日常生活。',
                    },
                  ].map((item, i) => (
                    <div key={i} className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                        <i className={`${item.icon} text-xl`}></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-16">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">
                  声誉与影响
                </h2>
                <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                    <div className="text-center">
                      <p className="font-heading text-3xl font-bold text-accent-600 mb-1">500,000+</p>
                      <p className="text-sm text-foreground-500">累计读者</p>
                    </div>
                    <div className="text-center">
                      <p className="font-heading text-3xl font-bold text-accent-600 mb-1">100+</p>
                      <p className="text-sm text-foreground-500">原创文章</p>
                    </div>
                    <div className="text-center">
                      <p className="font-heading text-3xl font-bold text-accent-600 mb-1">30+</p>
                      <p className="text-sm text-foreground-500">合作机构</p>
                    </div>
                  </div>

                  <div className="space-y-5">
                    {[
                      {
                        quote: '高义白洁网站的内容深入浅出，既有学术深度又兼顾可读性，是传统文化传播的优秀典范。',
                        author: '北京师范大学国学院',
                      },
                      {
                        quote: '在当下这个时代，能有这样一个专注于传统美德传播的平台，实属难得。每一篇文章都值得精读。',
                        author: '中华文化促进会',
                      },
                      {
                        quote: '作为教育工作者，我经常向学生推荐高义白洁的文章，它们不仅传授知识，更塑造品格。',
                        author: '知名人文学者 张教授',
                      },
                    ].map((item, i) => (
                      <div key={i} className="border-l-3 border-primary-300 pl-4 py-1">
                        <p className="text-foreground-700 leading-relaxed mb-1 italic">
                          "{item.quote}"
                        </p>
                        <p className="text-sm text-foreground-400">—— {item.author}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-5">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                      联系方式
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-map-pin-line"></i>
                        </div>
                        <div>
                          <p className="text-sm text-foreground-500">地址</p>
                          <p className="text-sm font-medium text-foreground-800">
                            {siteInfo.address.city}，{siteInfo.address.street}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                          <i className="ri-mail-line"></i>
                        </div>
                        <div>
                          <p className="text-sm text-foreground-500">邮箱</p>
                          <p className="text-sm font-medium text-foreground-800">{siteInfo.email}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                    <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                      给我们留言
                    </h3>
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form
                      className="space-y-4"
                    >
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          您的姓名
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          电子邮箱
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言（500字以内）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="company_alt"
                        autoComplete="off"
                        tabIndex={-1}
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                      >
                        {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                      </button>

                      {formStatus === 'success' && (
                        <p className="text-sm text-green-600 text-center">{formMessage}</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-accent-600 text-center">{formMessage}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}